import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  HelpCircle,
  MessageCircle,
  Phone,
  Mail,
  FileText,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Search,
  ExternalLink,
  Clock,
  Send,
  Headphones,
  Globe,
  GraduationCap,
  CreditCard,
  FileCheck,
  Plane,
  Shield,
  Users,
  CheckCircle2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

// FAQ Categories and Questions
const FAQ_CATEGORIES = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    icon: BookOpen,
    color: 'from-blue-500 to-cyan-400',
    questions: [
      {
        q: 'How do I create my student profile?',
        a: 'Navigate to "My Profile" from the sidebar menu. Fill in your personal information, educational background, and study abroad preferences. Make sure to save your changes after completing each section.',
      },
      {
        q: 'What documents do I need to upload?',
        a: 'Common documents include: Passport copy, academic transcripts, English proficiency test scores (IELTS/TOEFL), Statement of Purpose, Letters of Recommendation, and CV/Resume. Specific requirements may vary based on your chosen program.',
      },
      {
        q: 'How do I track my application status?',
        a: 'Go to "Track Services" in your dashboard. Here you can see all your active applications, their current status, and any pending actions required from your side.',
      },
    ],
  },
  {
    id: 'services',
    name: 'Our Services',
    icon: GraduationCap,
    color: 'from-purple-500 to-pink-400',
    questions: [
      {
        q: 'What services does Fly8 offer?',
        a: 'We offer comprehensive study abroad services including: Profile Assessment, Preapplication Support, Apply University, Visa & Interview Support, Ticket & Travel Support, Find Accommodation, Education Loan, and Find Jobs Abroad.',
      },
      {
        q: 'How long does the application process take?',
        a: 'The timeline varies by country and university. Generally, expect 2-4 weeks for document preparation, 4-8 weeks for university applications, and 2-6 weeks for visa processing. We recommend starting at least 6 months before your intended intake.',
      },
      {
        q: 'Can I apply to multiple universities?',
        a: 'Yes! We encourage applying to multiple universities to maximize your chances. Our counselors can help you create a balanced list of reach, match, and safety schools based on your profile.',
      },
    ],
  },
  {
    id: 'payments',
    name: 'Payments & Fees',
    icon: CreditCard,
    color: 'from-green-500 to-emerald-400',
    questions: [
      {
        q: 'What are the service fees?',
        a: 'Our fees vary based on the services you choose. Initial consultation is free. Service packages are customized based on your needs. Contact your counselor for a detailed fee structure.',
      },
      {
        q: 'What payment methods are accepted?',
        a: 'We accept multiple payment methods including Credit/Debit Cards, Bank Transfers, and popular digital wallets. All transactions are secured and encrypted.',
      },
      {
        q: 'Is there a refund policy?',
        a: 'Yes, we have a transparent refund policy. Refunds are processed based on the stage of service delivery. Please refer to your service agreement or contact support for specific details.',
      },
    ],
  },
  {
    id: 'visa',
    name: 'Visa & Immigration',
    icon: FileCheck,
    color: 'from-orange-500 to-amber-400',
    questions: [
      {
        q: 'Do you help with visa applications?',
        a: 'Yes! Our visa experts guide you through the entire process including document checklist, application form filling, interview preparation, and tracking your visa status.',
      },
      {
        q: 'What if my visa is rejected?',
        a: 'Our team will analyze the rejection reasons and help you prepare a stronger reapplication. We have a high success rate in visa approvals and extensive experience handling complex cases.',
      },
      {
        q: 'How early should I apply for a visa?',
        a: 'We recommend applying as soon as you receive your admission letter. Most student visas take 2-6 weeks to process, but it can be longer during peak seasons.',
      },
    ],
  },
];

// Quick Links
const QUICK_LINKS = [
  {
    title: 'University Guide',
    description: 'Explore top universities worldwide',
    icon: GraduationCap,
    color: 'from-blue-500 to-cyan-400',
    href: '/dashboard/services',
  },
  {
    title: 'Document Checklist',
    description: 'Essential documents for your application',
    icon: FileText,
    color: 'from-purple-500 to-pink-400',
    href: '/dashboard/track',
  },
  {
    title: 'Scholarship Guide',
    description: 'Find scholarships that match your profile',
    icon: Sparkles,
    color: 'from-amber-500 to-orange-400',
    href: '/dashboard/services',
  },
  {
    title: 'Pre-departure Tips',
    description: 'Prepare for your journey abroad',
    icon: Plane,
    color: 'from-green-500 to-emerald-400',
    href: '/dashboard/services',
  },
];

// Support channels
const SUPPORT_CHANNELS = [
  {
    title: 'Live Chat',
    description: 'Chat with our support team in real-time',
    icon: MessageCircle,
    availability: 'Available 24/7',
    color: 'from-sky-600 to-blue-500',
    action: 'Start Chat',
  },
  {
    title: 'Email Support',
    description: 'Get detailed responses via email',
    icon: Mail,
    availability: 'Response within 24 hours',
    color: 'from-purple-500 to-indigo-400',
    action: 'Send Email',
    email: 'support@fly8.com',
  },
  {
    title: 'Phone Support',
    description: 'Speak directly with our experts',
    icon: Phone,
    availability: 'Mon-Sat, 9 AM - 8 PM',
    color: 'from-green-500 to-emerald-400',
    action: 'Call Now',
    phone: '+1 (555) 123-4567',
  },
  {
    title: 'Video Consultation',
    description: 'Book a face-to-face online session',
    icon: Headphones,
    availability: 'By appointment',
    color: 'from-orange-500 to-amber-400',
    action: 'Schedule',
  },
];

export default function HelpSupport() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    priority: 'normal',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleQuestion = (categoryId, index) => {
    const key = `${categoryId}-${index}`;
    setExpandedQuestions(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleContactSubmit = async e => {
    e.preventDefault();
    if (!contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success(
      "Your message has been sent! We'll get back to you within 24 hours.",
    );
    setContactForm({ subject: '', message: '', priority: 'normal' });
    setIsSubmitting(false);
  };

  // Filter FAQs based on search query
  const filteredCategories = FAQ_CATEGORIES.map(category => ({
    ...category,
    questions: category.questions.filter(
      q =>
        q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.a.toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  })).filter(category => category.questions.length > 0);

  const activeQuestions =
    filteredCategories.find(c => c.id === activeCategory)?.questions ||
    filteredCategories[0]?.questions ||
    [];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="help-support-page">
        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-600 via-sky-500 to-indigo-600 p-8 lg:p-12 shadow-2xl shadow-sky-500/20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
          <div className="relative z-10 max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                24/7 Support Available
              </Badge>
            </div>
            <h1 className="text-3xl lg:text-4xl font-outfit font-bold text-white mb-3">
              How can we help you today?
            </h1>
            <p className="text-white/90 text-lg mb-6">
              Find answers to your questions, explore our guides, or get in
              touch with our support team.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for answers... (e.g., 'visa application', 'documents')"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-base bg-white/95 backdrop-blur-sm border-0 rounded-2xl shadow-lg focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute right-0 bottom-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4"></div>
          <div className="absolute right-1/4 top-0 w-32 h-32 bg-indigo-400/20 rounded-full blur-2xl"></div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK_LINKS.map((link, index) => {
            const Icon = link.icon;
            return (
              <Card
                key={index}
                className="group p-5 rounded-2xl hover:shadow-xl transition-all duration-300 cursor-pointer border border-slate-100 bg-white shadow-sm hover:-translate-y-1"
                onClick={() => (window.location.href = link.href)}
              >
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${link.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-sky-600 transition-colors">
                  {link.title}
                </h3>
                <p className="text-sm text-slate-500">{link.description}</p>
                <div className="flex items-center gap-1 mt-3 text-sm text-sky-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more <ArrowRight className="w-4 h-4" />
                </div>
              </Card>
            );
          })}
        </div>

        {/* Main Content - FAQ & Support */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* FAQ Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-outfit font-bold text-slate-900">
                  Frequently Asked Questions
                </h2>
                <p className="text-slate-500 mt-1">
                  Browse our most common questions by category
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {(searchQuery ? filteredCategories : FAQ_CATEGORIES).map(
                category => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        activeCategory === category.id
                          ? 'bg-gradient-to-r from-sky-600 to-blue-600 text-white shadow-md shadow-sky-500/25'
                          : 'bg-white text-slate-700 border border-slate-200 hover:border-sky-200 hover:bg-sky-50/50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {category.name}
                      <Badge
                        variant="secondary"
                        className={`ml-1 ${
                          activeCategory === category.id
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-200'
                        }`}
                      >
                        {category.questions.length}
                      </Badge>
                    </button>
                  );
                },
              )}
            </div>

            {/* FAQ Accordion */}
            <Card className="divide-y divide-slate-100 border border-slate-100 rounded-xl shadow-sm overflow-hidden">
              {activeQuestions.length > 0 ? (
                activeQuestions.map((faq, index) => {
                  const isExpanded =
                    expandedQuestions[`${activeCategory}-${index}`];
                  return (
                    <div key={index} className="group">
                      <button
                        onClick={() => toggleQuestion(activeCategory, index)}
                        className="w-full p-5 text-left flex items-start justify-between gap-4 hover:bg-slate-50/80 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isExpanded
                                ? 'bg-gradient-to-br from-sky-600 to-blue-600'
                                : 'bg-slate-100 group-hover:bg-sky-100'
                            }`}
                          >
                            <HelpCircle
                              className={`w-4 h-4 ${
                                isExpanded
                                  ? 'text-white'
                                  : 'text-slate-500 group-hover:text-sky-600'
                              }`}
                            />
                          </div>
                          <span className="font-medium text-slate-900 group-hover:text-sky-600 transition-colors">
                            {faq.q}
                          </span>
                        </div>
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                            isExpanded ? 'bg-sky-600' : 'bg-slate-100'
                          }`}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-white" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          isExpanded ? 'max-h-96' : 'max-h-0'
                        }`}
                      >
                        <div className="px-5 pb-5 pl-16">
                          <div className="p-4 bg-gradient-to-br from-sky-50/80 to-blue-50/80 rounded-xl border border-sky-100/50">
                            <p className="text-slate-700 leading-relaxed">
                              {faq.a}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    No results found
                  </h3>
                  <p className="text-slate-500 text-sm">
                    Try searching with different keywords or browse our
                    categories
                  </p>
                </div>
              )}
            </Card>

            {/* Still need help banner */}
            <Card className="p-6 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 border-0 text-white overflow-hidden relative shadow-xl shadow-purple-500/20">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMCAwaDQwdjQwSDB6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
              <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Still have questions?</h3>
                    <p className="text-white/80">
                      Our expert counselors are here to help you
                    </p>
                  </div>
                </div>
                <Button
                  className="bg-white text-indigo-600 hover:bg-white/90 font-semibold shadow-lg"
                  size="lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Chat with Counselor
                </Button>
              </div>
            </Card>
          </div>

          {/* Contact & Support Sidebar */}
          <div className="space-y-6">
            {/* Support Channels */}
            <Card className="p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="text-lg font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Headphones className="w-5 h-5 text-sky-600" />
                Contact Support
              </h3>
              <div className="space-y-3">
                {SUPPORT_CHANNELS.map((channel, index) => {
                  const Icon = channel.icon;
                  return (
                    <div
                      key={index}
                      className="group p-4 rounded-xl bg-slate-50/80 hover:bg-gradient-to-r hover:from-slate-50 hover:to-sky-50 transition-all duration-200 cursor-pointer border border-transparent hover:border-sky-100"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg bg-gradient-to-br ${channel.color} flex items-center justify-center flex-shrink-0`}
                        >
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors">
                            {channel.title}
                          </h4>
                          <p className="text-sm text-slate-500 mt-0.5">
                            {channel.description}
                          </p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-green-600 font-medium">
                            <Clock className="w-3 h-3" />
                            {channel.availability}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Contact Form */}
            <Card className="p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="text-lg font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-sky-600" />
                Send us a Message
              </h3>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <Label className="text-slate-700 text-sm">Subject *</Label>
                  <Input
                    value={contactForm.subject}
                    onChange={e =>
                      setContactForm({
                        ...contactForm,
                        subject: e.target.value,
                      })
                    }
                    placeholder="How can we help?"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Priority</Label>
                  <div className="flex gap-2 mt-1.5">
                    {['low', 'normal', 'high'].map(priority => (
                      <button
                        key={priority}
                        type="button"
                        onClick={() =>
                          setContactForm({ ...contactForm, priority })
                        }
                        className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium capitalize transition-all duration-200 ${
                          contactForm.priority === priority
                            ? priority === 'high'
                              ? 'bg-red-100 text-red-700 border-2 border-red-200'
                              : priority === 'normal'
                                ? 'bg-sky-100 text-sky-700 border-2 border-sky-200'
                                : 'bg-slate-100 text-slate-700 border-2 border-slate-200'
                            : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {priority}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Message *</Label>
                  <Textarea
                    value={contactForm.message}
                    onChange={e =>
                      setContactForm({
                        ...contactForm,
                        message: e.target.value,
                      })
                    }
                    placeholder="Describe your issue or question in detail..."
                    rows={4}
                    className="mt-1.5 resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white shadow-lg shadow-sky-500/25 transition-all duration-200"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* Trust Indicators */}
            <Card className="p-5 rounded-xl border border-slate-100 shadow-sm bg-gradient-to-br from-slate-50/80 to-white">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 rounded-xl bg-white">
                  <div className="text-2xl font-bold text-sky-600">24/7</div>
                  <div className="text-xs text-slate-500">Support Available</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-white">
                  <div className="text-2xl font-bold text-sky-600">
                    &lt;2hrs
                  </div>
                  <div className="text-xs text-slate-500">Avg Response Time</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-white">
                  <div className="text-2xl font-bold text-sky-600">98%</div>
                  <div className="text-xs text-slate-500">Satisfaction Rate</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-white">
                  <div className="text-2xl font-bold text-sky-600">50K+</div>
                  <div className="text-xs text-slate-500">Students Helped</div>
                </div>
              </div>
            </Card>

            {/* Emergency Support */}
            <Card className="p-5 rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/50">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-amber-900">
                    Emergency Support
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    For urgent visa or travel issues, call our 24/7 emergency
                    line:
                  </p>
                  <a
                    href="tel:+18001234567"
                    className="inline-flex items-center gap-2 mt-2 text-amber-900 font-bold hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    +880 1686-395927
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
